import { axisName, fmtMoney, fmtNum, getLang, translate, useT } from '../lib/i18n';
import { Fragment, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Component, type ReactNode } from 'react';

/**
 * Catches a render crash in one section so it does not black out the
 * whole tab. Shows what went wrong rather than nothing, which is the
 * difference between a bug report and a shrug.
 */
class SafeBox extends Component<{ children: ReactNode }, { err: string | null }> {
  state = { err: null as string | null };
  static getDerivedStateFromError(e: any) { return { err: String(e?.message ?? e) }; }
  render() {
    if (this.state.err) {
      return <div className="card__when" style={{ padding: 16 }}>{translate(getLang(), 'safe.couldNotShow', { message: this.state.err })}</div>;
    }
    return this.props.children;
  }
}
import * as api from '../lib/api';
import type { Booking, LedgerEntry, Me } from '../lib/api';
import { addDaysToKey, fmtDayNum, fmtLongDay, fmtMonthShort, fmtTime, fmtWeekday, relativeTo, toCoachDateKey } from '../lib/time';
import { alert, canUseDialogs, confirm, haptic, openExternal } from '../lib/telegram';
import { readableError } from './BookScreen';
import { useScrollFocus } from '../lib/scroll-focus';

/**
 * What the client wants to work on.
 *
 * Editable right up to the session: "actually, can we do serves" arrives
 * the night before at least as often as at booking time. The coach sees
 * it on their schedule and in the booking notification, which is most of
 * the value — knowing before the day rather than at the net.
 */
function NoteBox({ booking }: { booking: any }) {
  const t = useT();
  const [note, setNote] = useState(booking.client_note ?? '');
  const [saved, setSaved] = useState(booking.client_note ?? '');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await api.updateBookingNote(booking.id, note);
      setSaved(note);
      haptic.success();
    } catch (err: any) {
      haptic.error();
      await alert(err?.code === 'blocked' ? t('err.blocked') : t('note.failed'));
    } finally { setBusy(false); }
  }

  // Light on purpose: an inline line with a pencil, not a form field. It
  // becomes an input only in the sense that tapping it lets you type.
  return (
    <>
      <label className="bnote">
        <PenIcon />
        <input className="bnote__input"
          placeholder={t('book.workingOn')}
          value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {note !== saved && (
        <button className="savebtn" disabled={busy} onClick={save}>
          {busy ? t('note.saving') : t('note.save')}
        </button>
      )}
    </>
  );
}

/**
 * Subscribing to your own sessions, at the bottom of My profile.
 *
 * A subscription rather than a one-off export: new bookings and
 * cancellations flow through automatically, where a downloaded file goes
 * stale the moment anything changes. It keeps past sessions and drops
 * cancelled ones by itself.
 */
export function CalendarFeedRow() {
  const t = useT();
  const [webcal, setWebcal] = useState<string | null>(null);

  useEffect(() => {
    api.getMyCalendarUrl().then((r) => setWebcal(r.webcal)).catch(() => setWebcal(null));
  }, []);

  if (webcal === null) return null;

  return (
    <>
      <div className="section-title">{t('cal.feedTitle')}</div>
      <div className="list">
        <button className="calfeed" onClick={() => { haptic.tap(); subscribeToCalendar(webcal); }}>
          <svg className="calfeed__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3.5" y="5" width="17" height="15.5" rx="3" /><path d="M3.5 10h17M8 3v4M16 3v4" />
          </svg>
          <span className="calfeed__text">
            <span className="calfeed__name">{t('cal.feedName')}</span>
            <span className="calfeed__hint">{t('cal.feedHint')}</span>
          </span>
          <span className="calfeed__go">{t('cal.feedSubscribe')}</span>
        </button>
      </div>
    </>
  );
}

/**
 * Which calendar? Asked rather than guessed, because a phone's operating
 * system doesn't tell you which calendar someone actually uses — plenty
 * of iPhone owners live in Google Calendar.
 */
export async function pickCalendar(): Promise<'google' | 'apple' | null> {
  const w = (window as any).Telegram?.WebApp;
  // showPopup is 6.2 and up, and on an older client calling it throws
  // rather than failing quietly — the tap would do nothing at all.
  if (!w?.showPopup || !canUseDialogs()) {
    return window.confirm(translate(getLang(), 'cal.confirmGoogle')) ? 'google' : 'apple';
  }
  return new Promise((resolve) => {
    try {
      w.showPopup({
        title: translate(getLang(), 'cal.title'),
        message: translate(getLang(), 'cal.which'),
        buttons: [
          { id: 'google', type: 'default', text: translate(getLang(), 'cal.google') },
          { id: 'apple', type: 'default', text: translate(getLang(), 'cal.appleOther') },
          { id: '', type: 'cancel' },
        ],
      }, (id: string) => resolve((id as any) || null));
    } catch {
      resolve(window.confirm(translate(getLang(), 'cal.confirmGoogle')) ? 'google' : 'apple');
    }
  });
}

/**
 * Subscribe to a calendar feed, so it keeps itself up to date.
 *
 * Apple and Outlook subscribe on tap from a webcal:// link. Google does
 * not handle webcal at all, but its web app takes the feed as a "cid"
 * and asks to add it, which works from a phone's browser too.
 */
export async function subscribeToCalendar(webcal: string) {
  const which = await pickCalendar();
  if (which === 'google') {
    openExternal(`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}`);
  } else if (which === 'apple') {
    openExternal(webcal);
  }
}

/**
 * A client moving their own session.
 *
 * Only offered outside the cancellation window — same 24 hours as
 * cancelling, so nobody can move a session to next month and then cancel
 * it for free.
 *
 * Shows the coach's genuinely open slots rather than a free-text time,
 * so a client cannot put themselves somewhere the coach isn't working.
 */
/**
 * Bring a friend onto a session you have booked.
 *
 * The reply is the same sentence whatever happened, and the link is
 * always shown — because the moment the screen behaves differently for a
 * username that matched, it has told you who is a client.
 */
function InviteFriendSheet({ booking, onClose }: { booking: any; onClose: () => void }) {
  const t = useT();
  const [username, setUsername] = useState('');
  const [payer, setPayer] = useState<'me' | 'them'>('me');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ message: string; link: string } | null>(null);
  const [copied, setCopied] = useState(false);

  /**
   * Clipboard access is blocked in some Telegram webviews, so fall back
   * to a selection copy rather than leaving the tap doing nothing.
   */
  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const el = document.createElement('textarea');
      el.value = link;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); } catch { /* nothing else to try */ }
      document.body.removeChild(el);
    }
    haptic.success();
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  async function send() {
    if (!username.trim()) return;
    setBusy(true);
    try {
      const res = await api.inviteFriend(booking.id, username.trim(), payer);
      haptic.success();
      setDone({ message: res.message, link: res.link });
    } catch (err: any) {
      haptic.error();
      await alert(
        err.code === 'blocked' ? t('err.blocked')
        : err.code === 'session_full' ? t('invite.full')
        : err.code === 'session_has_started' ? t('invite.started')
        : err.code === 'invites_not_enabled' ? t('invite.notEnabled')
        : err.code === 'too_many_open_invites' ? t('invite.tooMany')
        : t('invite.failed'));
    } finally { setBusy(false); }
  }

  return createPortal(
    <div className="sheet">
      <div className="sheet__panel sheet--rhythm invite">
        {done ? (
          <>
            <div className="invite__hero">
              <span className="invite__badge invite__badge--sent">✉️</span>
              <div className="sheet__title">{t('invite.ready')}</div>
              {/* The server's one fixed reply, whoever the username belonged to. */}
              <div className="invite__msg">{done.message}</div>
            </div>

            <button type="button" className={`invite__link${copied ? ' invite__link--copied' : ''}`}
              onClick={() => copyLink(done.link)}>
              <span className="invite__url">{done.link}</span>
              <span className="invite__copy">{copied ? `${t('invite.copied')} ✓` : t('invite.copy')}</span>
            </button>
            <div className="invite__fine">{t('invite.tapToCopy')}</div>

            <div className="sheet__actions">
              <button className="paybar__opt" data-back onClick={onClose}>{t('invite.done')}</button>
              <button className="savebtn"
                onClick={() => openExternal(
                  `https://t.me/share/url?url=${encodeURIComponent(done.link)}`)}>
                {t('invite.share')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="invite__hero invite__hero--left">
              <span className="invite__badge">👋</span>
              <div>
                <div className="sheet__title">{t('invite.title')}</div>
                <div className="invite__session">
                  {booking.title} · {fmtLongDay(new Date(booking.starts_at))} {t.at()} {fmtTime(booking.starts_at)}
                </div>
              </div>
            </div>

            <label className="mf">
              <span className="mf__label">{t('invite.username')}</span>
              <span className="invite__handle">
                <span className="invite__at">@</span>
                <input placeholder={t('invite.usernamePh')} autoFocus autoCapitalize="none" autoCorrect="off"
                  spellCheck={false} value={username}
                  onChange={(e) => setUsername(e.target.value.replace(/^@+/, ''))} />
              </span>
            </label>

            <div>
              <span className="mf__label">{t('invite.whoPays')}</span>
              <div className="payer">
                <button type="button" className="payer__opt" aria-pressed={payer === 'me'}
                  onClick={() => { haptic.tap(); setPayer('me'); }}>
                  <span className="payer__icon">🎁</span>
                  <b>{t('invite.coverBoth')}</b>
                  <small>{t('invite.theyPayNothing')}</small>
                </button>
                <button type="button" className="payer__opt" aria-pressed={payer === 'them'}
                  onClick={() => { haptic.tap(); setPayer('them'); }}>
                  <span className="payer__icon">💳</span>
                  <b>{t('invite.theyPay')}</b>
                  <small>{t('invite.usualRate')}</small>
                </button>
              </div>
            </div>

            <div className="sheet__actions">
              <button className="paybar__opt" data-back onClick={onClose}>{t('cancel.back')}</button>
              <button className="savebtn"
                disabled={busy || !username.trim()} onClick={send}>
                {busy ? t('invite.sending') : t('invite.send')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function ClientMoveSheet({ booking, onClose, onDone }: {
  booking: any; onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const [slots, setSlots] = useState<any[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getSessionTypes()
      .then((types) => {
        const priv = types.find((t) => t.kind === 'private');
        if (!priv) { setSlots([]); return; }
        const from = new Date().toISOString().slice(0, 10);
        const to = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
        return api.getSlots(priv.id, from, to).then((r) => setSlots(r.slots ?? []));
      })
      .catch(() => setSlots([]));
  }, []);

  const byDay = new Map<string, any[]>();
  for (const sl of slots ?? []) {
    const k = new Date(sl.startsAt).toISOString().slice(0, 10);
    if (!byDay.has(k)) byDay.set(k, []);
    byDay.get(k)!.push(sl);
  }

  async function go() {
    if (!chosen) return;
    setBusy(true);
    try {
      await api.rescheduleBooking(booking.id, chosen);
      haptic.success();
      await alert(t('move.moved', {
        day: fmtLongDay(new Date(chosen)), time: fmtTime(chosen),
      }));
      onDone();
      onClose();
    } catch (err: any) {
      haptic.error();
      await alert(
        err.code === 'blocked' ? t('err.blocked')
        : err.code === 'too_late_to_move' ? t('move.tooLate')
        : err.code === 'slot_unavailable' ? t('move.slotGone')
        : t('move.failed'));
    } finally { setBusy(false); }
  }

  return createPortal(
    <div className="sheet">
      <div className="sheet__panel">
        <div className="sheet__title">{t('move.title')}</div>
        <div className="card__when">
          {t('move.currently', {
            day: fmtLongDay(new Date(booking.starts_at)), time: fmtTime(booking.starts_at),
          })}
        </div>

        <div className="sheet__label">{t('move.pickNew')}</div>
        <div style={{ maxHeight: '42vh', overflowY: 'auto' }}>
          {slots === null ? (
            <div className="skeleton" />
          ) : slots.length === 0 ? (
            <div className="card__when">{t('move.nothingOpen')}</div>
          ) : (
            [...byDay.entries()].map(([day, list]) => (
              <div key={day} style={{ marginBottom: 10 }}>
                <div className="card__when" style={{ marginBottom: 4 }}>
                  {fmtLongDay(new Date(day + 'T12:00:00'))}
                </div>
                <div className="repeat__opts" style={{ flexWrap: 'wrap' }}>
                  {list.map((sl) => (
                    <button key={sl.startsAt} className="chip"
                      aria-pressed={chosen === sl.startsAt}
                      onClick={() => { haptic.tap(); setChosen(sl.startsAt); }}>
                      {fmtTime(sl.startsAt)}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="paybar__opt" data-back style={{ flex: 1 }} onClick={onClose}>{t('cancel.back')}</button>
          <button className="paybar__opt"
            style={{ flex: 2, background: 'var(--accent)', color: 'var(--accent-text)', borderColor: 'var(--accent)' }}
            disabled={!chosen || busy} onClick={go}>
            {busy ? t('move.moving')
              : chosen ? t('move.moveTo', { time: fmtTime(chosen) })
              : t('move.pickATime')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * How the player is progressing.
 *
 * Drawn as a radar because the shape is the point — a player who is
 * strong everywhere except tactics sees that instantly, where six numbers
 * in a list would need reading.
 *
 * The eye is poor at judging area though, so the two lines that moved
 * most are named underneath in plain numbers. That is what a client
 * actually repeats to a friend: "my consistency went from 4 to 7".
 *
 * Absent entirely until there are ratings. An empty chart is a worse
 * first impression than no chart.
 */
export function ProgressChart({ clientId, titled = true, sport, heading }: {
  clientId: string;
  /** Off inside the coach's Progress tab, whose name already says it. */
  titled?: boolean;
  /** One sport's skills only, for a coach who teaches both (sql/046), under `heading`. */
  sport?: 'padel' | 'tennis';
  heading?: string;
}) {
  const t = useT();
  const [coachAxes, setAxes] = useState<api.AxisProgress[] | null>(null);
  const [matchAxes, setMatchAxes] = useState<api.AxisProgress[]>([]);

  useEffect(() => {
    api.getMyProgress(clientId)
      .then((r) => {
        const mine = sport ? r.filter((x) => (x.sport ?? 'padel') === sport) : r;
        setAxes(mine.filter((x) => x.rated_by === 'coach')); setMatchAxes(mine.filter((x) => x.rated_by === 'match'));
      })
      .catch(() => setAxes([]));
  }, [clientId, sport]);

  if (coachAxes === null || coachAxes.length === 0) return null;
  // Named once here, so the chart, the tiles and the lines under them agree.
  const axes = coachAxes.map((a) => ({ ...a, name: axisName(a) }));

  const size = 320;
  const cx = size / 2, cy = size / 2, radius = 100;
  const n = axes.length;

  const point = (i: number, value: number) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    const r = (Math.max(0, Math.min(10, value)) / 10) * radius;
    return [cx + Math.cos(angle) * r, cy + Math.sin(angle) * r];
  };
  const pts = (vals: number[]) =>
    vals.map((v, i) => point(i, v)).map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const poly = (get: (a: api.AxisProgress) => number | null) => pts(axes.map((a) => get(a) ?? 0));

  const hasHistory = axes.some((a) => a.score_before !== null);

  // The two axes that moved most, named plainly.
  /**
   * What changed, phrased usefully.
   *
   * "Net play got worse" is usually not true — more often the coach
   * started paying attention to it, or the player's game moved on and
   * the standard moved with it. Telling someone they have gone backwards
   * while they are training hard is demoralising and frequently wrong.
   * The weakest axis is named as the next thing to work on instead.
   */
  const rated = axes.filter((a) => a.score_before !== null);
  const rising = rated
    .map((a) => ({ name: a.name, from: a.score_before!, to: a.score, delta: a.score - a.score_before! }))
    .filter((m) => m.delta >= 0.4)
    .sort((x, y) => y.delta - x.delta)
    .slice(0, 2);
  const weakest = axes.slice().sort((x, y) => x.score - y.score)[0];

  return (
    <>
      {titled && <div className="section-title">{t('progress.title')}</div>}
      {heading && <div className="section-title">{heading}</div>}
      <div className="list">
        <div className="card radar">
          <svg viewBox={`0 14 ${size} ${size - 16}`} className="radar__svg" role="img"
            aria-label={axes.map((a) => `${a.name} ${fmtNum(a.score, 1)}`).join(', ')}>
            <defs>
              <radialGradient id="radarFill" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.1" />
                <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.38" />
              </radialGradient>
            </defs>

            {/* The web: banded rings, outermost first so each band paints over the last. */}
            {[10, 8, 6, 4, 2].map((ring, k) => (
              <polygon key={ring} points={pts(axes.map(() => ring))}
                className={k % 2 === 0 ? 'radar__band' : 'radar__band radar__band--alt'} />
            ))}
            {axes.map((_, i) => {
              const [x, y] = point(i, 10);
              return <line key={i} x1={cx} y1={cy} x2={x} y2={y} className="radar__spoke" />;
            })}

            <g className="radar__shape">
              {hasHistory && (
                <polygon points={poly((a) => a.score_before)} className="radar__before" />
              )}
              <polygon points={poly((a) => a.score)} className="radar__now" fill="url(#radarFill)" />

              {/* Match shape, red, drawn over training. The gap between the
                  two is the point: where blue is big and red is small, the
                  skill exists in practice and dies under pressure. */}
              {matchAxes.length > 0 && (
                <polygon className="radar__match"
                  points={pts(axes.map((a) => matchAxes.find((x) => x.axis_id === a.axis_id)?.score ?? 0))} />
              )}

              {axes.map((a, i) => {
                const [x, y] = point(i, a.score);
                return <circle key={a.axis_id} cx={x} cy={y} r="4.5" className="radar__dot" />;
              })}
            </g>

            {axes.map((a, i) => {
              const [x, y] = point(i, 12.6);
              const anchor = x < cx - 6 ? 'end' : x > cx + 6 ? 'start' : 'middle';
              const up = y < cy - radius * 0.9;
              return (
                <text key={a.axis_id} x={x} y={up ? y - 8 : y} textAnchor={anchor} className="radar__label">
                  <tspan x={x} className="radar__name">{a.name}</tspan>
                  <tspan x={x} dy="14" className="radar__score">{fmtNum(a.score, 1)}</tspan>
                </text>
              );
            })}
          </svg>

          {(hasHistory || matchAxes.length > 0) && (
            <div className="radar__legend">
              <span><i className="radar__key radar__key--now" />{t('progress.now')}</span>
              {hasHistory && <span><i className="radar__key radar__key--before" />{t('progress.lastMonth')}</span>}
              {matchAxes.length > 0 && <span><i className="radar__key radar__key--match" />{t('progress.matches')}</span>}
            </div>
          )}

          {/*
            Every axis as a tile: where they are, a bar to read it at a
            glance, and whether it is moving. The arrow is the part a
            player actually wants to know.
          */}
          <div className="axisgrid">
            {axes.map((a) => {
              const delta = a.score_before === null ? null : a.score - a.score_before;
              const dir = delta === null ? '' : delta >= 0.3 ? 'up' : delta <= -0.3 ? 'down' : 'flat';
              const m = matchAxes.find((x) => x.axis_id === a.axis_id);
              return (
                <div className="axisgrid__cell" key={a.axis_id}>
                  <span className="axisgrid__name">{a.name}</span>
                  <div className="axisgrid__row">
                    <b>{fmtNum(a.score, 1)}</b>
                    {m && <i className="axisgrid__d axisgrid__d--match">/ {fmtNum(m.score, 1)}</i>}
                    {dir === 'up' && <i className="axisgrid__chip axisgrid__chip--up">▲ {fmtNum(Math.abs(delta!), 1)}</i>}
                    {dir === 'down' && <i className="axisgrid__chip axisgrid__chip--down">▼ {fmtNum(Math.abs(delta!), 1)}</i>}
                  </div>
                  <div className="axisgrid__bar"><span style={{ width: `${Math.min(100, a.score * 10)}%` }} /></div>
                </div>
              );
            })}
          </div>

          {(rising.length > 0 || weakest) && (
            <div className="radar__summary">
              {rising.length > 0 && (
                <>
                  {rising.map((m) => t('progress.upTo', { skill: m.name, score: m.to.toFixed(1) })).join(' · ')}
                  {weakest && '. '}
                </>
              )}
              {weakest && t('progress.focusNext', { skill: weakest.name })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Logging a match.
 *
 * Result and score are the facts. "How did it feel" is the part that
 * matters for coaching: stress, a partner who would not talk, a first
 * set won then handed back. The player writes it while it is fresh.
 *
 * Requesting a review is opt-in and priced, because it is an hour of
 * the coach's time watching footage — a real service, not a feature.
 *
 * The same sheet corrects one. A match is typed on a phone straight
 * after playing and the score comes out wrong; until the coach has
 * reviewed it, it is still the player's to fix. After that the sheet
 * is not offered — the review was written against what it says.
 */
function MatchForm({ fee, match, onClose, onDone }: {
  fee: number; match?: any; onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const today = new Date().toISOString().slice(0, 10);
  const [playedOn, setPlayedOn] = useState(
    match?.played_on ? String(match.played_on).slice(0, 10) : today);
  const [result, setResult] = useState<'won' | 'lost' | null>(match?.result ?? null);
  const [score, setScore] = useState(match?.score ?? '');
  const [opponent, setOpponent] = useState(match?.opponent ?? '');
  const [partner, setPartner] = useState(match?.partner ?? '');
  const [feeling, setFeeling] = useState(match?.client_feeling ?? '');
  const [link, setLink] = useState(match?.link ?? '');
  const [review, setReview] = useState(Boolean(match?.review_requested));
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!result) { await alert(t('match.needResult')); return; }
    if (review && !link.trim()) { await alert(t('match.needLink')); return; }
    setBusy(true);
    try {
      const body = {
        played_on: playedOn, result, score, opponent, partner,
        feeling, link, request_review: review,
      };
      if (match) await api.editMatch(match.id, body);
      else await api.logMatch(body);
      haptic.success();
      await alert(match ? t('match.saved')
        : review ? t('match.loggedReview', { fee })
        : t('match.logged'));
      onDone();
      onClose();
    } catch (err: any) {
      haptic.error();
      await alert(err.code === 'blocked' ? t('err.blocked') : t('match.saveFailed', { message: err.message }));
    } finally { setBusy(false); }
  }

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel matchform" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{match ? t('match.editTitle') : t('match.formTitle')}</div>
        <div className="matchform__sub">{match ? t('match.editSub') : t('match.formSub')}</div>

        <div className="result">
          <button type="button" className="result__opt result__opt--won" aria-pressed={result === 'won'}
            onClick={() => { haptic.tap(); setResult('won'); }}>
            <span className="result__icon">🏆</span>{t('match.won')}
          </button>
          <button type="button" className="result__opt result__opt--lost" aria-pressed={result === 'lost'}
            onClick={() => { haptic.tap(); setResult('lost'); }}>
            <span className="result__icon">👎🏼</span>{t('match.lost')}
          </button>
        </div>

        <div className="matchform__row">
          <label className="mf">
            <span className="mf__label">{t('match.date')}</span>
            <input type="date" value={playedOn} onChange={(e) => setPlayedOn(e.target.value)} />
          </label>
          <label className="mf">
            <span className="mf__label">{t('match.score')}</span>
            <input placeholder="6-4 3-6 6-2" value={score} inputMode="text"
              onChange={(e) => setScore(e.target.value)} />
          </label>
        </div>

        <div className="matchform__row">
          <label className="mf">
            <span className="mf__label">{t('match.partner')}</span>
            <input placeholder={t('match.namePh')} value={partner} onChange={(e) => setPartner(e.target.value)} />
          </label>
          <label className="mf">
            <span className="mf__label">{t('match.against')}</span>
            <input placeholder={t('match.namesPh')} value={opponent} onChange={(e) => setOpponent(e.target.value)} />
          </label>
        </div>

        <label className="mf">
          <span className="mf__label">{t('match.feel')}</span>
          <textarea rows={3} value={feeling} onChange={(e) => setFeeling(e.target.value)}
            placeholder={t('match.feelHint')} />
        </label>

        <div className={`reviewopt${review ? ' reviewopt--on' : ''}`}>
          <button type="button" className="reviewopt__head" role="switch" aria-checked={review}
            onClick={() => { haptic.tap(); setReview(!review); }}>
            <span className="reviewopt__text">
              <b>{t('match.askReview')}</b>
              <small>{t('match.reviewBlurb')}</small>
            </span>
            <span className="reviewopt__price">{fmtMoney(fee)}</span>
            <span className="switch" aria-hidden="true"><span className="switch__knob" /></span>
          </button>
          {review && (
            <input className="reviewopt__link" type="url" inputMode="url" autoFocus
              placeholder={t('match.pasteLink')} value={link} onChange={(e) => setLink(e.target.value)} />
          )}
        </div>

        <div className="sheet__actions">
          <button className="paybar__opt" onClick={onClose}>{t('match.cancel')}</button>
          <button className="savebtn" disabled={busy} onClick={save}>
            {busy ? t('match.saving')
              : match ? t('match.saveEdit')
              : review ? t('match.saveFee', { fee })
              : t('match.save')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Matches on My profile, above Credits. */
export function MatchesSection({ clientId }: { clientId: string }) {
  return <SafeBox><MatchList clientId={clientId} /></SafeBox>;
}

/** The player's match history, with the coach's notes where they exist. */
function MatchList({ clientId }: { clientId: string }) {
  const t = useT();
  const [rows, setRows] = useState<any[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [fee, setFee] = useState(20);

  const load = () => api.getMyMatches().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, [clientId]);
  useEffect(() => {
    api.getMe().then((m: any) => { if (m.matchReviewFee) setFee(Number(m.matchReviewFee)); }).catch(() => {});
  }, []);

  return (
    <>
      <div className="section-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{t('match.title')}</span>
        <button className="balance__action" onClick={() => setAdding(true)}>{t('match.log')}</button>
      </div>
      {/*
        Said every time, not only when the list is empty: the reason to
        log is not the record, it is that the coach sees how training
        holds up in a real match and plans the next sessions around it.
      */}
      <div className="matchwhy">
        <span className="matchwhy__icon">📈</span>
        <span>
          <b>{t('match.whyLead')}</b>{t('match.whyBody')}
        </span>
      </div>
      <div className="list">
        {rows === null ? <div className="skeleton" /> :
         rows.length === 0 ? null : rows.map((m) => (
          <div className="card" key={m.id}>
            <div className="card__head">
              <span className="card__title" style={{ color: m.result === 'won' ? 'var(--court)' : 'var(--held)' }}>
                {m.result === 'won' ? t('match.won') : t('match.lost')}{m.score ? ` ${m.score}` : ''}
              </span>
              <span className="card__when">{m.played_on ? fmtLongDay(new Date(String(m.played_on).slice(0, 10) + 'T12:00:00')) : ''}</span>
            </div>
            {(m.opponent || m.partner) && (
              <div className="card__when" style={{ marginTop: 3 }}>
                {m.opponent ? t('match.vs', { name: m.opponent }) : ''}
                {m.partner ? t('match.with', { name: m.partner }) : ''}
              </div>
            )}
            {/* What they wrote after the match, kept and shown back to them. */}
            {m.client_feeling && (
              <div className="card__when" style={{ marginTop: 6 }}>
                <b>{t('match.youWrote')}</b> {m.client_feeling}
              </div>
            )}
            {m.reviewed_at ? (
              <>
                {/* The coach's own words follow the label and are never translated. */}
                {m.coach_worked && <div className="clientnote" style={{ marginTop: 8 }}><b>{t('match.whatWorked')}</b> {m.coach_worked}</div>}
                {m.coach_didnt && <div className="clientnote" style={{ marginTop: 6 }}><b>{t('match.toWorkOn')}</b> {m.coach_didnt}</div>}
                {m.ratings?.length > 0 && (
                  <>
                    <div className="card__when" style={{ marginTop: 8 }}>{t('match.coachRated')}</div>
                    <div className="matchscores">
                      {/* A skill name is the coach's own word: never translated. */}
                      {m.ratings.map((r: any) => (
                        <span className="matchscore" key={r.name}>
                          {r.name}<b>{r.score}</b>
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : m.review_requested ? (
              <div className="card__when" style={{ marginTop: 6 }}>{t('match.reviewWaiting')}</div>
            ) : null}
            {/* Theirs to correct until the coach has reviewed it. */}
            {!m.reviewed_at && (
              <button className="notelink" style={{ marginTop: 6 }}
                onClick={() => { haptic.tap(); setEditing(m); }}>{t('match.edit')}</button>
            )}
          </div>
        ))}
      </div>
      {adding && <MatchForm fee={fee} onClose={() => setAdding(false)} onDone={load} />}
      {editing && (
        <MatchForm fee={fee} match={editing}
          onClose={() => setEditing(null)} onDone={load} />
      )}
    </>
  );
}

/** Today, Tomorrow or Later, by the coach's calendar day. */
function dayGroup(iso: string): 'today' | 'tomorrow' | 'later' {
  const key = toCoachDateKey(new Date(iso));
  const today = toCoachDateKey(new Date());
  if (key === today) return 'today';
  if (key === addDaysToKey(today, 1)) return 'tomorrow';
  return 'later';
}

/**
 * The name on a booking card.
 *
 * A private session is named from its length, in the player's language:
 * the stored type names were typed in English and carry a length of
 * their own, which goes stale when the coach changes it. A group class
 * keeps the coach's own name.
 */
function sessionName(b: Booking, t: ReturnType<typeof useT>): string {
  if (b.kind !== 'private') return b.title;
  const min = Math.round((new Date(b.ends_at).getTime() - new Date(b.starts_at).getTime()) / 60_000);
  return t('bookings.private', { min });
}

/** «два», «три»: Russian counts lessons in words up to ten (занятие is neuter: «одно»). */
const RU_NEUTER = ['', 'одно', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять', 'десять'];
const countWord = (n: number) => (getLang() === 'ru' && n >= 1 && n <= 10 ? RU_NEUTER[n] : String(n));

/**
 * «2 кредита на счету» and «Записаны вперёд: 3 — два по абонементу, одно
 * оплачивается на занятии (80 €)» (§2.8). The numbers are the server's
 * walletState(); about running short as such, nothing — only what it costs.
 */
function MyCoverage({ credits, c }: { credits: number; c: NonNullable<Me['coverage']> }) {
  const t = useT();
  const amount = fmtMoney(c.cashDue);
  const parts = c.needsPayment === 0 ? [t('me.allCovered')]
    : c.covered === 0 ? [t('me.dueAll', { amount })]
    : [t('me.coveredPart', { w: countWord(c.covered) }),
       c.needsPayment === 1 ? t('me.duePart1', { amount }) : t('me.duePartN', { w: countWord(c.needsPayment), amount })];
  return (
    <div className="mycover">
      <div className="mycover__big">{fmtNum(credits)}<small>{t.word('me.onAccount', credits)}</small></div>
      <div className="mycover__line">
        {t('me.ahead', { n: c.planned })} — {parts.map((p, i) => (
          <Fragment key={i}>
            {i > 0 ? ', ' : ''}
            {/* The part still to pay is the one in amber. */}
            <span className={i === 1 || c.covered === 0 ? 'due' : undefined}>{p}</span>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export function BookingsScreen({ onChanged, onBook, credits = 0, coverage = null }: {
  onChanged: () => void; onBook: () => void;
  /** Slice 2: the balance and how the lessons ahead split; null hides the card. */
  credits?: number; coverage?: Me['coverage'];
}) {
  const t = useT();
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [moving, setMoving] = useState<any | null>(null);
  const [inviting, setInviting] = useState<any | null>(null);

  async function load() {
    setBookings(await api.getBookings(false));
  }
  useEffect(() => {
    load();
  }, []);

  async function cancel(b: Booking) {
    const deadline = new Date(b.free_cancel_until).getTime();
    const late = Date.now() > deadline;

    const ok = await confirm(
      late
        ? b.payment_mode === 'cash'
          ? t('cancel.lateWarningCash', { amount: b.amount_due ?? 0 })
          : t('cancel.lateWarning')
        : t('cancel.confirm', { title: sessionName(b, t), day: fmtLongDay(new Date(b.starts_at)) }),
    );
    if (!ok) return;

    try {
      const res = await api.cancelBooking(b.id);
      haptic.success();
      await alert(res.message);
      // The cancellation is done. A failure reloading the list is not a
      // failed cancellation, and reporting it as one tells the client
      // their session is still booked when it isn't.
      void Promise.resolve().then(() => onChanged()).then(() => load()).catch(() => {});
    } catch (err) {
      haptic.error();
      await alert(readableError(err));
    }
  }

  if (!bookings) {
    return (
      <div className="list">
        <div className="skeleton" />
        <div className="skeleton" />
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="bempty">
        <div className="bempty__icon">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3.5" y="5" width="17" height="15.5" rx="3" /><path d="M3.5 10h17M8 3v4M16 3v4M12 13.2v4M10 15.2h4" />
          </svg>
        </div>
        <div className="bempty__lead">{t('bookings.none')}</div>
        <div className="bempty__hint">{t('bookings.noneHint')}</div>
        <button className="bempty__btn" onClick={() => { haptic.tap(); onBook(); }}>{t('bookings.book')}</button>
      </div>
    );
  }

  const groups = (['today', 'tomorrow', 'later'] as const)
    .map((g) => ({ g, rows: bookings.filter((b) => dayGroup(b.starts_at) === g) }))
    .filter((x) => x.rows.length > 0);

  return (
    <>
    {coverage && <MyCoverage credits={credits} c={coverage} />}
    {inviting && (
      <InviteFriendSheet booking={inviting} onClose={() => setInviting(null)} />
    )}
    {moving && (
      <ClientMoveSheet booking={moving}
        onClose={() => setMoving(null)}
        onDone={() => { onChanged(); load(); }} />
    )}
    {groups.map(({ g, rows }) => (
      <div key={g}>
        <div className="section-title">{t(`bookings.${g}`)}</div>
        <div className="blist">
          {rows.map((b) => {
            const start = new Date(b.starts_at);
            const late = Date.now() > new Date(b.free_cancel_until).getTime();
            const waiting = b.status === 'waitlisted';
            /*
              A place in a queue is not a session: there is nothing to
              move it to, and nobody to bring along to a class you are
              not in yet. Leaving the queue stays one tap away.
            */
            const canMove = !waiting && !late;
            const canInvite = !waiting && start.getTime() > Date.now();
            return (
              <div className="bcard" key={b.id}>
                <div className="bcard__top">
                  <div className={`bcard__tile${g === 'today' ? ' bcard__tile--today' : ''}`}>
                    <span className="bcard__wd">{fmtWeekday(start).replace('.', '')}</span>
                    <span className="bcard__day">{fmtDayNum(start)}</span>
                    <span className="bcard__mo">{fmtMonthShort(start).replace('.', '')}</span>
                  </div>
                  <div className="bcard__main">
                    <div className="bcard__timerow">
                      <span className="bcard__time">{fmtTime(b.starts_at)}</span>
                      <span className="bcard__end">– {fmtTime(b.ends_at)}</span>
                      {g === 'today' && (
                        <span className="bcard__soon">{t('bookings.in', { time: relativeTo(b.starts_at) })}</span>
                      )}
                    </div>
                    <div className="bcard__name">{sessionName(b, t)}</div>
                    {b.location && <div className="bcard__where">{b.location}</div>}
                  </div>
                </div>

                <div className="bcard__info">
                  {waiting ? (
                    <div className="bline"><ClockIcon />{t('bookings.waitlist', { n: b.waitlist_position ?? 0 })}</div>
                  ) : (
                    <>
                      <div className="bline bline--pay">
                        {b.payment_mode === 'cash' ? <CashIcon /> : <CreditIcon />}
                        {b.payment_mode === 'cash' && Number(b.credits_charged ?? 0) > 0 && Number(b.credit_value ?? 0) > 0
                          ? t('bookings.toPaySplit', { amount: Math.round(Number(b.amount_due ?? 0)), covered: Math.round(Number(b.credit_value)) })
                          : b.payment_mode === 'cash'
                          ? t('bookings.toPay', { amount: b.amount_due ?? 0 })
                          // On credits, but the package does not reach it: what it will cost.
                          : b.pay === 'due' && b.pay_due != null
                          ? t('bookings.toPay', { amount: b.pay_due })
                          : t('lv.nCredits', { n: b.credits_charged, credits: t.word('credits.word', Number(b.credits_charged)) })}
                      </div>
                      <div className="bline">
                        <ClockIcon />
                        {late
                          ? t('bookings.freeCancelPassed')
                          : t('bookings.freeCancelFor', { time: relativeTo(b.free_cancel_until) })}
                      </div>
                    </>
                  )}
                  <NoteBox booking={b} />
                </div>

                {(canMove || canInvite) && (
                  <div className="bcard__actions">
                    {canMove && (
                      <button className="bbtn" onClick={() => { haptic.tap(); setMoving(b); }}>
                        <MoveIcon />{t('bookings.move')}
                      </button>
                    )}
                    {canInvite && (
                      <button className="bbtn" onClick={() => { haptic.tap(); setInviting(b); }}>
                        <InviteIcon />{t('bookings.addFriend')}
                      </button>
                    )}
                  </div>
                )}
                {/* Quiet and set apart from the buttons, so it is not hit by accident. */}
                <button className="bcard__cancel" onClick={() => cancel(b)}>
                  {t('bookings.cancel')}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    ))}
    </>
  );
}

const PenIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16z" /></svg>
);
const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg>
);
const CreditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    aria-hidden="true"><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5v9" /></svg>
);
const CashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" aria-hidden="true"><rect x="3" y="6.5" width="18" height="11" rx="2.5" /><circle cx="12" cy="12" r="2.4" /></svg>
);
const MoveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 7h11l-3-3M17 17H6l3 3" /></svg>
);
const InviteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c.8-3.6 3.4-5.5 6.5-5.5s5.7 1.9 6.5 5.5M19 8v6M16 11h6" /></svg>
);

/**
 * Credits, at the bottom of My profile: packages, then every movement.
 *
 * `focusAt` changes when the credits line on Book is tapped. The section
 * then scrolls itself into view — more than once, because the profile and
 * the journal above it are still loading and push it down as they arrive.
 */
export function CreditsSection({ me, focusAt }: { me: Me; focusAt?: number }) {
  const t = useT();
  const [history, setHistory] = useState<LedgerEntry[] | null>(null);
  const here = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.getCreditHistory().then(setHistory);
  }, []);

  useScrollFocus(here, focusAt);

  return (
    <div ref={here} id="credits" className="credits">
      <div className="mp-title credits__title">{t('credits.title')}</div>
      {me.packages && me.packages.length > 0 && (
        <>
          <div className="section-title">{t('credits.packages')}</div>
          <div className="list">
            {me.packages.map((p) => (
              <div className="card" key={p.id}>
                <div className="card__head">
                  <span className="card__title">
                    {t('credits.leftCount', { n: p.balance, credits: t.word('credits.word', p.balance) })}
                  </span>
                  <span className="card__when">
                    {p.applies_to.includes('private') && p.applies_to.includes('group')
                      ? t('credits.anySession')
                      : p.applies_to.includes('group')
                        ? t('credits.groupOnly')
                        : t('credits.privateOnly')}
                  </span>
                </div>
                {p.expires_at && (
                  <div className="card__when" style={{ marginTop: 3 }}>
                    {t('credits.expires', {
                      day: fmtLongDay(new Date(p.expires_at)),
                      left: relativeTo(p.expires_at),
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <div className="section-title">{t('credits.history')}</div>
      <div className="list">
        {history === null ? (
          <>
            <div className="skeleton" />
            <div className="skeleton" />
          </>
        ) : history.length === 0 ? (
          <div className="empty">
            <div className="empty__lead">{t('credits.none')}</div>
            {t('credits.noneHint')}
          </div>
        ) : (
          history.map((h) => (
            <div className="ledger" key={h.id}>
              <div className="ledger__what">
                <div>{describeLedger(h)}</div>
                <div className="ledger__when">{fmtLongDay(new Date(h.created_at))}</div>
              </div>
              <div
                className={`ledger__delta ${h.delta > 0 ? 'ledger__delta--plus' : 'ledger__delta--minus'}`}
              >
                {Number(h.delta) > 0 ? '+' : '−'}
                {fmtNum(Math.abs(Number(h.delta)))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function describeLedger(h: LedgerEntry): string {
  // Not a component: reads the language directly rather than through the hook.
  const tr = (key: Parameters<typeof translate>[1], vars?: Record<string, string | number>) =>
    translate(getLang(), key, vars);
  switch (h.reason) {
    case 'purchase':
      return tr('ledger.purchase');
    case 'booking':
      // The session title is the coach's own wording and stays as typed.
      return h.session_starts_at && h.session_title
        ? tr('ledger.booked', { title: h.session_title, time: fmtTime(h.session_starts_at) })
        : tr('ledger.bookedPlain');
    case 'cancellation_refund':
      return tr('ledger.refund');
    case 'expiry':
      return tr('ledger.expiry');
    case 'no_show_charge':
      return tr('ledger.noShow');
    case 'package_removed':
      return tr('ledger.packageRemoved');
    default:
      return tr('ledger.adjustment');
  }
}
